import { jsPDF } from 'jspdf';
import { savePDF } from './savePDF';

const MAROON   = [142, 44,  44];
const GOLD     = [212, 177, 90];
const NAVY     = [31,  60,  136];
const DARKGRAY = [60,  60,  60];

const formatDateLong = (dateStr) => {
  if (!dateStr) return '____';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const getBase64Image = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

/**
 * Generate and download the Classic DRP Certificate as a PDF.
 *
 * @param {Object} data
 * @param {string} data.studentName      - e.g. "John Doe"
 * @param {string} data.courseName       - e.g. "General Medicine"
 * @param {string} data.hospitalName     - e.g. "District Hospital Indore"
 * @param {string|Date} data.fromDate    - posting start date
 * @param {string|Date} data.toDate      - posting end date
 * @param {string} data.inchargeName     - DRP incharge full name
 * @param {string} collegeName           - from collegeConfig
 * @param {string} logoSrc               - from collegeConfig.logo
 */
export const generateClassicDRPCertificate = async (data, collegeName, logoSrc) => {
  const { studentName, courseName, hospitalName, fromDate, toDate, inchargeName } = data;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // ── Borders ──────────────────────────────────────────────────────────────
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(3);
  doc.rect(8, 8, W - 16, H - 16);
  doc.setLineWidth(1);
  doc.rect(12, 12, W - 24, H - 24);

  // ── Top maroon banner ─────────────────────────────────────────────────────
  doc.setFillColor(...MAROON);
  doc.rect(12, 12, W - 24, 52, 'F');

  // Logo — centered
  const logoBase64 = await getBase64Image(logoSrc);
  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', W / 2 - 14, 13, 28, 28); }
    catch (_) { /* skip */ }
  }

  // College name — centered below logo
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const nameLines = doc.splitTextToSize(collegeName, W - 60);
  doc.text(nameLines, W / 2, 48, { align: 'center' });

  // ── Gold separator ────────────────────────────────────────────────────────
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.2);
  doc.line(15, 66, W - 15, 66);

  // ── Certificate title ─────────────────────────────────────────────────────
  doc.setTextColor(...MAROON);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.text('CERTIFICATE', W / 2, 82, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(12);
  doc.setTextColor(...GOLD);
  doc.text('of Completion — District Residency Programme', W / 2, 90, { align: 'center' });

  // ── Certificate body ──────────────────────────────────────────────────────
  const bodyText =
    `This is to Certify that Dr. ${studentName || '____'} ` +
    `Department of ${courseName || '____'} ` +
    `was posted at ${hospitalName || '____'} ` +
    `From ${formatDateLong(fromDate)} to ${formatDateLong(toDate)}. ` +
    `He/She has successfully completed the District Residency Programme ` +
    `and hence certified.`;

  doc.setFont('times', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(...DARKGRAY);
  const bodyLines = doc.splitTextToSize(bodyText, W - 60);
  doc.text(bodyLines, W / 2, 105, { align: 'center', lineHeightFactor: 1.7 });

  // ── Gold separator above signatures ───────────────────────────────────────
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(15, H - 52, W - 15, H - 52);

  // ── Signature area ────────────────────────────────────────────────────────
  const col1 = W / 4;
  const col2 = W - W / 4;
  const sigY = H - 28;

  // Date (left)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Date', col1, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col1 - 35, sigY - 4, col1 + 35, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(formatDateLong(toDate), col1, sigY, { align: 'center' });

  // Name of Incharge (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Name of Incharge', col2, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col2 - 40, sigY - 4, col2 + 40, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(inchargeName || '', col2, sigY, { align: 'center' });

  // ── Footer ─────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...NAVY);
  doc.text('District Residency Programme — Classic DRP Certificate', W / 2, H - 16, { align: 'center' });

  await savePDF(doc, `DRP_Certificate_${(studentName || 'Student').replace(/\s+/g, '_')}.pdf`);
};
